import { computed, effect, inject, Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith, tap } from 'rxjs';
import { AuthRoute, UnauthRoutes } from 'src/models/routes.model';
import { UserDataService } from 'src/data-store/user-data.service';
import { DeviceService } from 'src/services/device.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
import { BWM_BG_DARK, BWM_BG_LIGHT } from 'src/util/constants';

enum ThemeType {
  bwm = 'bwm',
  bwm_dark = 'bwm-dark',
  liberro = 'liberro',
  liberro_dark = 'liberro-dark',
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public currentTheme = environment.appBuildMode;
  private userDataService = inject(UserDataService);
  private deviceService = inject(DeviceService);
  private router = inject(Router);
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  private readonly lightOnlyRoutes = [
    `/${AuthRoute.LOGIN}`,
    ...Object.values(UnauthRoutes).map(route => `/${route}`),
  ];
  public alwaysLight = computed(() => {
    const current = this.currentUrl();
    return this.lightOnlyRoutes.some(route => current.includes(route));
  });
  private theme = computed<ThemeType>(() =>  (!this.userDataService.isDarkMode() || this.alwaysLight() ? this.currentTheme : (this.currentTheme + '-dark')) as ThemeType);
  private initialized = false;

  public constructor() {
    effect(() => this.loadTheme(!this.initialized, this.theme()));
  }

  private reverseTheme(theme: string): ThemeType {
    switch(theme) {
      case ThemeType.bwm:
        return ThemeType.bwm_dark;
      case ThemeType.bwm_dark:
        return ThemeType.bwm;
      case ThemeType.liberro:
        return ThemeType.liberro_dark;
      case ThemeType.liberro_dark:
        return ThemeType.liberro;
      default:
        throw new Error('Unsupported theme: ' + theme);
    }
  }

  private setThemeActive(theme: ThemeType, active: boolean, before?: HTMLLinkElement | null): void {
    const link = document.getElementById(theme) as HTMLLinkElement | null;
    if (active) {
      if (!link) {
        this.loadCss(`${theme}.css`, theme, before);
      }
      document.documentElement.classList.add(theme);
    } else {
      link?.remove();
      document.documentElement.classList.remove(theme);
    }
  }

  private loadCss(href: string, id: string, before?: HTMLLinkElement | null): Promise<Event> {
    return new Promise((resolve, reject) => {
      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.href = href;
      style.id = id;
      style.onload = resolve;
      style.onerror = reject;
      // Keep theme CSS before runtime CDK overlay styles (append would break tooltip repositioning).
      if (before?.parentNode) {
        before.parentNode.insertBefore(style, before);
      } else {
        document.head.append(style);
      }
    });
  }

  private updateNativeSystemBars(theme: ThemeType): void {
    if (!this.deviceService.isNativePlatform) {
      return;
    }

    const isDark = theme.endsWith('-dark');

    // Style.Dark = light icons for dark backgrounds; Style.Light = dark icons for light backgrounds
    StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    NavigationBar.setNavigationBarColor({
      color: isDark ? BWM_BG_DARK : BWM_BG_LIGHT,
      darkButtons: !isDark,
    });
  }

  public loadTheme(firstLoad: boolean = true, theme: ThemeType = this.theme()): void {
    if (firstLoad) {
      this.initialized = true;
    }
    const previousTheme = this.reverseTheme(theme);
    const previousLink = document.getElementById(previousTheme) as HTMLLinkElement | null;
    this.setThemeActive(theme, true, previousLink);
    this.setThemeActive(previousTheme, false);
    this.updateNativeSystemBars(theme);
  }
}
